import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Close } from "@material-ui/icons";
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  KeyboardTimePicker,
} from "@material-ui/pickers";
import React from "react";
import { Controller, useWatch } from "react-hook-form";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { format, parse } from "date-fns";

const screeningDefaultValue = {
  startDate: format(new Date(), "yyyy-MM-dd"),
  startTime: format(new Date(), "HH:mm"),
  endDate: format(new Date(), "yyyy-MM-dd"),
  endTime: format(new Date(), "HH:mm"),
  startDateTime: format(new Date(), "yyyy-MM-dd HH:mm"),
  endDateTime: format(new Date(), "yyyy-MM-dd HH:mm"),
  type: "offline",
  remark: [],
  remarks: [],
  url: "",
  localizations: {
    en: { title: "", filmCode: "" },
    tc: { title: "", filmCode: "" },
  },
};

const useStyles = makeStyles((theme) => ({
  screeningBlk: {
    background: "#FFFFFF",
    marginBottom: "20px",
  },
  screeningBlkHeader: {
    background: "#EDEEF0",
    borderLeft: "3px solid #9D3030",
    fontWeight: "bold",
    fontSize: "14px",
    lineHeight: "16px",
    padding: "12px 13px",
  },
  screeningBlkBody: {
    border: "1px solid #CCCCCC",
    borderTop: "0px",
    padding: "20px 13px",
  },
  deleteBtn: {
    margin: "0",
    padding: "0",
    minWidth: "initial",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
}));

const ScreeningBlock = ({
  control,
  index,
  classes,
  disabled,
  remove,
  data,
  setValue,
  lang,
  remarkList,
}) => {
  const screening = useWatch({
    control,
    name: `screening[${index}]`,
  });

  return (
    <>
      <Grid
        container
        className={classes.screeningBlkHeader}
        xs={12}
        alignItems="center"
        justify="space-between"
      >
        <span>Screening {index + 1}</span>
        {disabled || (
          <Button className={classes.deleteBtn} onClick={() => remove(index)}>
            <Close style={{ fontSize: "14px", lineHeight: "16px" }} />
          </Button>
        )}
      </Grid>
      <Box className={classes.screeningBlkBody}>
        <Grid container spacing={3} direction="column">
          <Grid container item xs={12} spacing={3}>
            <Grid container item xs={6} spacing={3}>
              <Grid
                item
                xs={6}
                style={{
                  display:
                    screening && screening.type === "offline"
                      ? "block"
                      : "none",
                }}
              >
                <Controller
                  name={`screening[${index}].startDate`}
                  defaultValue={data.startDate}
                  control={control}
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState: { error },
                  }) => (
                    <KeyboardDatePicker
                      fullWidth
                      disabled={disabled}
                      variant="inline"
                      format="yyyy-MM-dd"
                      margin="dense"
                      id={`startDate-picker${index}`}
                      label="Date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      value={
                        screening?.startDate
                          ? parse(screening.startDate, "yyyy-MM-dd", new Date())
                          : null
                      }
                      onChange={(date, value) => {
                        // onChange(value);
                        setValue(`screening[${index}].startDate`, value);
                      }}
                      error={!!error}
                      helperText={error ? error.message : ""}
                      {...rest}
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  display:
                    screening && screening.type === "online" ? "block" : "none",
                }}
              >
                <Controller
                  name={`screening[${index}].startDateTime`}
                  defaultValue={
                    data.startDate && data.startTime
                      ? `${data.startDate} ${data.startTime}`
                      : ""
                  }
                  control={control}
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState: { error },
                  }) => {
                    return (
                      <KeyboardDateTimePicker
                        fullWidth
                        minutesStep={5}
                        disabled={disabled}
                        variant="inline"
                        format="yyyy-MM-dd HH:mm"
                        margin="dense"
                        id={`startDateTime-picker${index}`}
                        label="Start Date"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                        {...rest}
                        value={
                          screening?.startDate && screening?.startTime
                            ? `${screening.startDate} ${screening.startTime}`
                            : data?.startDate && data?.startTime
                            ? `${data.startDate} ${data.startTime}`
                            : null
                        }
                        onChange={(date, value) => {
                          // onChange(value);
                          const dateTimeArg = value.split(" ");
                          setValue(
                            `screening[${index}]`,
                            {
                              startDate: dateTimeArg[0],
                              startTime: dateTimeArg[1],
                            },
                            { shouldDirty: true }
                          );
                        }}
                        error={!!error}
                        helperText={error ? error.message : ""}
                        views={["year", "month", "date", "hours", "minutes"]}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  display:
                    screening && screening.type === "offline"
                      ? "block"
                      : "none",
                }}
              >
                <Controller
                  defaultValue={data.startTime ? data.startTime : null}
                  name={`screening[${index}].startTime`}
                  control={control}
                  render={({
                    field: { value, onChange, ...rest },
                    fieldState: { error },
                  }) => {
                    return (
                      <KeyboardTimePicker
                        fullWidth
                        minutesStep={5}
                        disabled={disabled}
                        variant="inline"
                        format="HH:mm"
                        margin="dense"
                        id={`startTime-picker${index}`}
                        label="Time"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        KeyboardButtonProps={{
                          "aria-label": "change time",
                        }}
                        value={
                          screening?.startTime
                            ? parse(screening.startTime, "HH:mm", new Date())
                            : null
                        }
                        onChange={(date, value) => {
                          // onChange(value);
                          setValue(`screening[${index}].startTime`, value);
                        }}
                        error={!!error}
                        helperText={error ? error.message : ""}
                        views={["hours", "minutes"]}
                        {...rest}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  display:
                    screening && screening.type === "online" ? "block" : "none",
                }}
              >
                <Controller
                  defaultValue={
                    data.endDate && data.endTime
                      ? `${data.endDate} ${data.endTime}`
                      : ""
                  }
                  name={`screening[${index}].endDateTime`}
                  control={control}
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState: { error },
                  }) => (
                    <KeyboardDateTimePicker
                      fullWidth
                      minutesStep={5}
                      disabled={disabled}
                      variant="inline"
                      format="yyyy-MM-dd HH:mm"
                      margin="dense"
                      id={`endDateTime-picker${index}`}
                      label="End Date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      {...rest}
                      value={
                        !!screening?.endDate && !!screening?.endTime
                          ? `${screening.endDate} ${screening.endTime}`
                          : data?.endDate && data?.endTime
                          ? `${data.endDate} ${data.endTime}`
                          : null
                      }
                      onChange={(date, value) => {
                        const dateTimeArg = value.split(" ");
                        console.log(dateTimeArg);
                        setValue(`screening[${index}].endDate`, dateTimeArg[0]);
                        setValue(`screening[${index}].endTime`, dateTimeArg[1]);
                      }}
                      renderInput={(props) => (
                        <TextField label="Date" helperText="Something" />
                      )}
                      error={!!error}
                      helperText={error ? error.message : ""}
                      views={["year", "month", "date", "hours", "minutes"]}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container item xs={3} alignItems="center">
              <Controller
                defaultValue={data?.type}
                name={`screening[${index}].type`}
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={screening?.type}
                    onChange={(event, newFormats) => {
                      if (newFormats) {
                        // onChange(newFormats);
                        setValue(`screening[${index}].type`, newFormats);
                      }
                    }}
                    exclusive
                    aria-label="period status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="online"
                      aria-label="online"
                      disabled={disabled}
                    >
                      Online Screening
                    </ToggleButton>
                    <ToggleButton
                      value="offline"
                      aria-label="offline"
                      disabled={disabled}
                    >
                      Offline Screening
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Controller
              defaultValue={
                data.remarks &&
                remarkList &&
                remarkList.filter((o) =>
                  data.remarks.map((p) => p.id).includes(o.id)
                )
              }
              name={`screening[${index}].remarks`}
              control={control}
              render={({
                field: { onChange, value, ...rest },
                fieldState: { error },
              }) => {
                return (
                  <Autocomplete
                    multiple
                    fullWidth
                    label="Remark"
                    options={remarkList}
                    disabled={disabled}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) =>
                      option.localizations[`${lang}`].content
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Remarks"
                        margin="dense"
                        placeholder="Remarks"
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                    value={remarkList.filter((o) =>
                      screening?.remarks?.map((p) => p.id).includes(o.id)
                    )}
                    onChange={(e, values) => {
                      // onChange(values);
                      setValue(`screening[${index}].remarks`, values);
                    }}
                    {...rest}
                  />
                );
              }}
            />
          </Grid>
          <Grid container item xs={12}>
            <Controller
              defaultValue={data?.localizations?.[lang]?.url}
              name={`screening[${index}].localizations.${lang}.url`}
              control={control}
              render={({
                field: { value, onChange, ...rest },
                fieldState: { error },
              }) => (
                <TextField
                  label="Buy Ticket URL"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  error={!!error}
                  helperText={error ? error.message : ""}
                  InputLabelProps={{ shrink: true }}
                  {...rest}
                  onChange={(event) => {
                    setValue(`screening[${index}].localizations.${lang}.url`, event.target.value);
                  }}
                  value={screening?.localizations?.[lang]?.url}
                />
              )}
            />
          </Grid>
          <Grid container item xs={12}>
            <Grid container item xs={6}>
              <Controller
                defaultValue={data?.localizations?.[lang]?.title}
                name={`screening[${index}].localizations.${lang}.title`}
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <TextField
                    label="Free Text"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : 'It will override the "Buy Ticket" button if filled.'
                    }
                    InputLabelProps={{ shrink: true }}
                    {...rest}
                    onChange={(event) => {
                      // onChange(event.target.value);
                      setValue(
                        `screening[${index}].localizations.${lang}.title`,
                        event.target.value
                      );
                    }}
                    value={screening?.localizations?.[lang]?.title}
                  />
                )}
              />
            </Grid>
            <Grid container item xs={6}>
              <Controller
                defaultValue={data?.localizations?.[lang]?.filmCode}
                name={`screening[${index}].localizations.${lang}.filmCode`}
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <TextField
                    label="Film Code"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    error={!!error}
                    helperText={error ? error.message : ""}
                    InputLabelProps={{ shrink: true }}
                    {...rest}
                    onChange={(event) => {
                      // onChange(event.target.value);
                      setValue(
                        `screening[${index}].localizations.${lang}.filmCode`,
                        event.target.value
                      );
                    }}
                    value={screening?.localizations?.[lang]?.filmCode}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default function ScreeningSections(props) {
  const {
    control,
    lang,
    setValue,
    remarkList,
    disabled,
    fields,
    remove,
    append,
  } = props;

  const classes = useStyles();

  return (
    <>
      {fields.map((data, index) => (
        <div className={classes.screeningBlk} key={data.id}>
          <ScreeningBlock
            {...{
              control,
              index,
              classes,
              disabled,
              remove,
              data,
              setValue,
              lang,
              remarkList,
            }}
          />
        </div>
      ))}
      {disabled || (
        <Button
          type="button"
          onClick={() => {
            append(screeningDefaultValue);
          }}
          style={{
            color: "#306F9D",
            margin: "5px 0",
          }}
        >
          <AddCircleIcon />
          Add Screening
        </Button>
      )}
    </>
  );
}
